import React from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useTimeModal } from '../../TimeModal/TimeModalContext';
import { formatNull, formatDate, formatOverflow, formatDateTime } from '../../../Utils/GlobalUtils';

import Loading from '../../Loading';
import LoadingLines from '../../LoadingLines';
import WarningPanel from '../../WarningPanel';

import IconContainer from '../../IconContainer';
import { ReactComponent as ReloadIcon } from '../../../icons/reload.svg';

const ProceduresCreatedByAMs = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { openTimeModal } = useTimeModal();

    const [cases, setCases] = React.useState([]);
    const [warning, setWarning] = React.useState('');
    const [reload, setReload] = React.useState(true);
    const [loading, setLoading] = React.useState(true);

    const [limitForView, setLimitForView] = React.useState(50);

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        setLoading(true);

        axios.get(`/api/get-cases-created-by-ams?limitForView=${limitForView}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        }
        })
        .then((response) => {
            setWarning('');
            setCases(response.data);
        })
        .catch((error) => {
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
            setReload(false);
        })
    }, [reload]);

    const [statusLoading, setStatusLoading] = React.useState(null);

    const handleApproveCaseBySRO = (id) => {
        const token = localStorage.getItem('token');

        setStatusLoading(id);

        axios.put(`/api/change-is-viewed-by-sro`, {
            case_id: id,
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(() => {
            openTimeModal('Карточка подтверждена СРО', 'success');
            setReload(true);
        }).catch((error) => {
            console.error(error);
            openTimeModal('Не удалось подтвердить', 'error');
        }).finally(() => {
            setStatusLoading(null);
        })
    };


    return (
        <>
        <div className='horizontalContainer'>
            <div className='boxPage'>
                <div className='boxHeader'>
                    <h3>Информация</h3>
                </div>
                <div className='boxMain' style={{fontSize: '14px'}}>
                    <div>Данная таблица отображает карточки дел, созданные арбитражными управляющими самостоятельно. После подтверждения СРО записи пропадают из этой таблицы.</div>
                </div>
            </div>
        </div>
        <div className='tableContainer'>        
            <div className='tableHeaderWrapper'>
                <div className='tableStatus'>
                    <h3>Карточки, созданные управляющими</h3>
                    <a className='lastUpdateWrapper'>
                        {loading ? 
                            <Loading type='inline' /> 
                        : 
                            <IconContainer Icon={ReloadIcon} onClick={() => {setReload(true); setLoading(true)}} size = '14' color = 'black' className = '' />
                        }
                    </a>
                </div>
                <div className='tableHeader' style={{ gridTemplateColumns: '11fr 13fr 20fr 10fr 14fr 14fr 10fr 10fr' }}>
                    <div>Номер</div>
                    <div>Процедура / Должник</div>
                    <div>Арбитражный суд / Управляющий</div>
                    <div>Дата и время создания карточки</div>
                    <div>Дата судебного заседания об утверждении АУ</div>
                    <div>Дата утверждения управляющего АС</div>
                    <div>Начальная дата</div>
                    <div></div>
                </div>
            </div>
            {(cases.length==0 && loading) ? <LoadingLines /> 
            : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить' buttonAction={() => { setReload(true); setLoading(true) }} />
            : cases.map((caseItem) => (
                <motion.div
                    key={caseItem.id}
                    className='tableRow nonActive'
                    style={{ gridTemplateColumns: '11fr 13fr 20fr 10fr 14fr 14fr 10fr 10fr' }}
                    initial={{ scale: '97%', opacity: 0 }}
                    animate={{ scale: '100%', opacity: 1 }}
                    exit={{ scale: '97%', opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                        <div>
                            <Link
                                className='aWithLink'
                                to={`/case/${caseItem.id}`}
                            >
                                {formatNull(caseItem.number)} 
                            </Link>
                            {caseItem.nameCategory === 'Жалоба' && '(Жалоба)'} 
                        </div>
                        <div>
                            <p>{formatNull(caseItem.procedure_name)}</p>
                            <span className='horizontalLine'></span>
                            {caseItem.debtors ?
                                <p>
                                    {formatOverflow(caseItem.debtors.map((e) => e.name).join(', '))}
                                </p>
                                :
                                formatNull(caseItem.debtors)
                            }
                        </div>
                        <div>
                            <p>{formatNull(caseItem.court_name)}</p>
                            <span className='horizontalLine'></span>
                            {caseItem.user_lastname ? 
                            <p>{`${caseItem.user_lastname} ${formatNull(caseItem.user_firstname)} ${formatNull(caseItem.user_surname)} ${formatNull(caseItem.user_email)}`}</p>
                            : 
                            <p className='colorCell red'>Управляющий не выбран</p>}
                        </div>
                        <div>
                            {formatDateTime(caseItem.created_at)}
                        </div>
                        <div>
                            {formatDate(caseItem.date_court)}
                        </div>
                        <div>
                            <p>{formatDate(caseItem.date_am_appointment_m)}</p>
                            <p className='description'>(полная версия)</p>
                            <p>{formatDate(caseItem.date_am_appointment_r)}</p>
                            <p className='description'>(рез. часть)</p>
                        </div>
                        <div>
                            {formatDate(caseItem.date_start)}
                        </div>
                        <div>
                            {statusLoading ?
                            <Loading />
                            :
                            <button
                                type='button'
                                className='tableButton'
                                onClick={() => handleApproveCaseBySRO(caseItem.id)}
                            >
                                Подтвердить СРО
                            </button>}
                        </div>
                </motion.div>
            ))}
            {!warning && cases.length > 0 && (cases.length < cases[0].total_count) && (
                <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div></div>
                    <div>
                        <button
                            type='button'
                            className='button'
                            style={{ width: 'fit-content' }}
                            onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                            disabled={loading}
                        >
                            {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                        </button>
                    </div>
                    <div></div>
                </div>
            )}
        </div>
    </>
  )
}

export default ProceduresCreatedByAMs;
